import { configs } from '@constants/configs/configs';
import { APP_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { history } from '@store/appStore';
import { getQLEStates, getDMIStates } from '@utils/miscellaneous/states';

const { endpoints, externalEndpoints, states, routes } = configs;

const checkIfSubmittedAppExists = (checkPriorSubmittedApp, ssapParams) => {
    const { applicationYear, cmrHouseholdId, caseNumber } = ssapParams;
    // callback in case no submitted application exists or error
    const cb = goToSSAPStart.bind(null, { caseNumber, applicationYear });
    checkPriorSubmittedApp({ cmrHouseholdId, coverageYear: applicationYear }, cb);
};

const goToStartApp = (ssapParams, actions, isStateFullStack) => {
    if(isStateFullStack) {
        checkIfSubmittedAppExists(actions.checkPriorSubmittedApp, ssapParams);
    } else {
        goToContactUs(states.MINNESOTA);
    }
};

const goToResumeApp = (stateCode, ssapParams, isStateFullStack) => {
    if(isStateFullStack) {
        goToSSAPStart(ssapParams);
    } else {
        window.open(externalEndpoints[stateCode.toUpperCase() +'_LINKS'][stateCode.toUpperCase() + '_NEW_CUSTOMERS'], '_blank');
    }
};


const goToContinueApp = (ssapParams, isStateFullStack) => {
    if (isStateFullStack) {
        goToSSAPStart(ssapParams);
    } else {
        goToContactUs(states.MINNESOTA, isStateFullStack);
    }
};

const goToGroupingScreen = ({
    tobaccoEnabled,
    hardshipEnabled,
    encryptedCaseNumber,
    applicationStatus,
    isTobaccoInformationAvailableForAllUser,
    shouldRedirectToCustomGrouping,
}) => {
    let path = routes.CUSTOM_GROUPING;

    if (tobaccoEnabled || hardshipEnabled) {
        path = `${configs.routes.TOBACCO_EXEMPT}?caseNumber=${encryptedCaseNumber}`;
        if (applicationStatus !== APP_STATUSES.ER && tobaccoEnabled && isTobaccoInformationAvailableForAllUser && shouldRedirectToCustomGrouping) {
            path = routes.CUSTOM_GROUPING;
        }
    }
    history.push(path);
};

const goToUploadDocs = (caseNumber, stateCode, isMNFullStack, selectedApplicantGuid) => {
    const dmiStates = getDMIStates(isMNFullStack);
    const isDmiState = dmiStates.includes(stateCode);
    if (isDmiState) {
        const path = `${configs.routes.DMI_VERIFICATIONS}?caseNumber=${caseNumber}`;
        const state = selectedApplicantGuid ? { selectedApplicantGuid } : undefined;
        history.push(path, state);
    } else {
        history.push(`${configs.routes.QLE_DOCUMENTS_MN}?caseNumber=${caseNumber}`);
    }
};

const goToQLEVerifications = (caseNumber, language, stateCode, isMNFullStack) => {
    const qleStates = getQLEStates(isMNFullStack);
    const isQleState = qleStates.includes(stateCode);
    if (isQleState) {
        history.push(`${routes.VERIFICATION_DOCUMENTS}?caseNumber=${caseNumber}&language=${language}`);
    } else {
        history.push(`${configs.routes.QLE_DOCUMENTS_MN}?caseNumber=${caseNumber}`);
    }
};

const goToViewDetails = (applicationYear) => {
    history.push(
        routes.MY_ENROLLMENT_HISTORY,
        applicationYear ? { coverageYear: applicationYear } : undefined,
    );
};

const goToPlanSummary = (caseNumber) => {
    history.push(routes.PLAN_SUMMARY, { caseNumber });
};

const goToApplyForCostSavings = () => {
    window.location = endpoints.IDA_LINK;
};

const goToContactUs = (stateCode, isStateFullStack) => {
    if (!isStateFullStack) {
        window.open(externalEndpoints[stateCode + '_LINKS'][stateCode + '_CONTACT_US'], '_blank');
    }
};

const goToContinue = (stateCode, isStateFullStack) => {
    if (!isStateFullStack && stateCode === states.MINNESOTA) {
        window.open(externalEndpoints.MN_LINKS.MN_DOCUMENTS, '_blank');
    }
};

const goToConfirmEvent = encryptedCaseNumber => {
    window.location = endpoints.LCE_REFERRAL.processReplacements({ encryptedCaseNumber });
};

const goToSSAPStart = ssapParams => {
    const { caseNumber, applicationYear, fromMyApp } = ssapParams;
    // Quick fix to match what has been done in HIX-123228.
    // Need a proper refactoring, instead of passing 'fromMyApp' param, we need to replace it with history API.
    const suffix = fromMyApp ? '&fromMyApp=true' : '';
    const URL = caseNumber
        ? endpoints.START_SSAP_BY_CASE_NUMBER.processReplacements({ caseNumber })
        : endpoints.START_SSAP_BY_YEAR.processReplacements({ applicationYear });
    window.location = URL + suffix;
};

const defaultReportAChange = (ssapParams, actions) => {
    const { encryptedApplicationId, applicationStatus, caseNumber } = ssapParams;

    const cloneApp = () => {
        const url =
            (encryptedApplicationId &&
                endpoints.CLONE_APP_BY_SSAP_ID.processReplacements({ encryptedApplicationId })) ||
            null;
        url && actions.cloneApplication(url, goToSSAPStart, ssapParams);
    };

    if ([APP_STATUSES.SG, APP_STATUSES.SU].includes(applicationStatus)) {
        actions.changeAppStatus(endpoints.CHANGE_APP_STATUS.processReplacements({ caseNumber }), cloneApp);
    } else {
        cloneApp();
    }
};

const goToExceptionalEditApplication = (caseNumber, copyApplication, customGroupingParams) => {
    const url = endpoints.COPY_APP_BY_CASE_NUMBER.processReplacements({ caseNumber });
    copyApplication(url, goToGroupingScreen, customGroupingParams);
};

const goToReportAChange = (stateCode, ssapParams, actions, isStateFullStack) => {
    if(isStateFullStack) {
        defaultReportAChange(ssapParams, actions);
    } else {
        window.open(externalEndpoints[stateCode + '_LINKS'][stateCode + '_REPORT_CHANGE'], '_blank');
    }
};

const goToViewAppSummary = (stateCode, ssapParams) => {
    goToSSAPStart(ssapParams);
};

const goToEligibilityResults = (stateCode, encryptedCaseNumber) => {
    history.push(configs.routes.ELIGIBILITY_RESULTS, {
        encryptedCaseNumber,
        prevLocation: configs.routes.APPLICATIONS,
    });
};

export const ctaHandlers = {
    goToStartApp,
    goToContinueApp,
    goToReportAChange,
    goToSSAPStart,
    goToApplyForCostSavings,
    goToGroupingScreen,
    goToResumeApp,
    goToContactUs,
    goToContinue,
    goToUploadDocs,
    goToViewDetails,
    goToConfirmEvent,
    goToPlanSummary,
    goToViewAppSummary,
    goToEligibilityResults,
    goToExceptionalEditApplication,
    goToQLEVerifications,
    defaultReportAChange,
};
